import { AgGridReact } from '@ag-grid-community/react';
import { Grid, Snackbar } from '@mui/material';
import { TableOverlay, useAgGrid } from '@tassat/ag-grid';
import { SelectOption } from '@tassat/common-definitions';
import { StyledSelect } from '@tassat/formik-input';
import { WalletRecordExtended } from '@tassat/fred-extended-definitions';
import { browserSessionStore } from '@tassat/utility';
import axios from 'axios';
import moment from 'moment';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react';
import ReactGA from 'react-ga4';
import { SingleValue } from 'react-select';

import { getAuthConfiguration } from 'config/variables';
import encryptDecrypt from 'Lib/encryptDecryptToken';
import { obscureAccountNo } from 'Lib/stringHelpers';
import Worker from 'Lib/worker/PrimusWorker';
import { useAppSelector } from 'Reducers/store/hooks';
import { getMappedUserAccountsByUserIdClient } from 'Selectors/userAccounts';
import { PageHeaderTitle } from 'SharedComponents/pageHeader';

import { columnDefs } from './accountGridOptions';
import { DownloadCellRenderer } from './downloadCellRenderer';

import type { DocumentCenterProps, DocumentDownloadStatus } from '.';

export const PAGE_TITLE = 'Document Center';

export const DocumentCenter = (props: DocumentCenterProps) => {
    const { classes } = props;
    const accounts = useAppSelector((state) => getMappedUserAccountsByUserIdClient(state, browserSessionStore.getItem('userid') ?? '', '', false, true));

    const [ selectedAccount, setSelectedAccount ] = useState<SelectOption['value'] | null>(null);
    const [ isSelectDisabled, setIsSelectDisabled ] = useState(false);
    const [ documents, setDocuments ] = useState<any[]>([]);
    const [ selectedDocuments, setSelectedDocuments ] = useState<any[]>([]);
    const [ isNotificationOpen, setIsNotificationOpen ] = useState(false);
    const [ snackbarContent, setSnackbarContent ] = useState({
        title: '',
        status: ''
    });
    const [ documentListStatus, setDocumentListStatus ] = useState<DocumentDownloadStatus>('noselection');

    const [ _, onGridReady ] = useAgGrid();

    const accountSelectOptions: SelectOption[] = useMemo(() => {
        const selectableAccounts: SelectOption[] = [];
        const selectableAccountIds: Array<string | undefined> = [];
        accounts.forEach(wallet => {
            if (!selectableAccountIds.includes((wallet.account as WalletRecordExtended).linkedBrokerDealerAccount)) {
                selectableAccountIds.push((wallet.account as WalletRecordExtended).linkedBrokerDealerAccount);
                selectableAccounts.push({
                    label: `${obscureAccountNo((wallet.account as WalletRecordExtended).linkedBrokerDealerAccount)}`,
                    value: wallet.account.AccountId
                });
            }
        });

        return selectableAccounts;
    }, [accounts]);

    useEffect(() => {
        if (accountSelectOptions.length === 1) {
            setSelectedAccount(accountSelectOptions[0].value);
            setIsSelectDisabled(true);
        }
        if (accountSelectOptions.length > 1) {
            setIsSelectDisabled(false);
        }
    }, [accountSelectOptions]);

    const selectAccount = (selectedAccountOption: SingleValue<SelectOption>) => {
        setSelectedAccount(selectedAccountOption?.value ? selectedAccountOption.value : null);
    };

    const getDocumentList = useCallback(async () => {
        try {
            if (selectedAccount) {
                setDocuments([]);
                const userAccountMapped = accounts.find(account => account.account.AccountId === selectedAccount);
                const accountBd = (userAccountMapped?.account as WalletRecordExtended)?.linkedBrokerDealerAccount;
                setDocumentListStatus('fetching');
                const response = await Worker.getInstance()
                    .getDocuments({
                        accountBd,
                        accountId: selectedAccount,
                        token: browserSessionStore.getItem('id_token')
                    });
                if (response.data.Documents) {
                    setDocuments(response.data.Documents);
                    setDocumentListStatus('fetched');
                } else if (response.data.Status !== 200){
                    setDocumentListStatus('failed');
                    setDocuments([]);
                }
            }
        } catch (error) {
            setDocumentListStatus('failed');
            setDocuments([]);
        }
    }, [selectedAccount]);

    const downloadDocument = async ({ data }) => {
        try {
            if (!selectedDocuments.includes(data.id)) {
                ReactGA.event('click', {
                    event_category: 'engagement',
                    event_label: 'document_center_download_document'
                });
                setSelectedDocuments([ ...selectedDocuments, data.id ]);
                setIsNotificationOpen(true);
                setSnackbarContent({
                    title: 'Download In Progress',
                    status: 'success'
                });

                const config = await getAuthConfiguration();
                const requestTime = moment()
                    .valueOf();

                const getTimeKey = () => {
                    const timestamp = requestTime.toString()
                        .substring(0, 10);

                    return timestamp.split('')
                        .reverse()
                        .join('');
                };
                const userAccountMapped = accounts.find(account => account.account.AccountId === selectedAccount);
                const accountBd = (userAccountMapped?.account as WalletRecordExtended)?.linkedBrokerDealerAccount;

                const response = await axios.get('doccenter', {
                    responseType: 'blob',
                    headers: { 'Content-Disposition': 'attachment' },
                    params: {
                        RequestTime: requestTime,
                        DocumentId: encodeURIComponent(encryptDecrypt.encrypt(data.id, getTimeKey(), config.TOKEN_SALT)),
                        AccountId: encodeURIComponent(encryptDecrypt.encrypt(selectedAccount as string, getTimeKey(), config.TOKEN_SALT)),
                        AccountNumber: encodeURIComponent(encryptDecrypt.encrypt(accountBd ?? '', getTimeKey(), config.TOKEN_SALT))
                    }
                });

                const url = window.URL.createObjectURL(response.data);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${data.title}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                handleClose({}, '');
                setIsNotificationOpen(true);
                setSnackbarContent({
                    title: 'Download Complete',
                    status: 'success'
                });
            }
        } catch (error) {
            setSnackbarContent({
                title: 'Download Failed',
                status: 'error'
            });
        } finally {
            setSelectedDocuments(prev => prev.filter(doc => doc !== data.id));
        }
    };

    const handleClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setIsNotificationOpen(false);
        setSnackbarContent({
            title: '',
            status: ''
        });
    };

    useEffect(() => {
        getDocumentList();
    }, [selectedAccount]);

    const noAccounts = accountSelectOptions.length === 0;

    const getNoRowsText = (): string => {
        let text = '';
        if (documentListStatus === 'noselection') {
            text = 'Select an account to retrieve documents.';
        }

        if (documentListStatus === 'fetching') {
            text = 'Retrieving documents...';
        }

        if (documentListStatus === 'failed') {
            text = 'Your request cannot be processed at the moment, please try again later. ' +
                'If issue persists, please contact client support.';
        }

        if (documentListStatus === 'fetched') {
            text = 'No documents available.';
        }

        if (noAccounts) {
            text = 'No accounts to show yet.';
        }

        return text;
    };

    const noOptionsText = 'No options available';

    return (
        <Grid
            className={classes.container}
            container
            direction='column'
            spacing={0}
            wrap='nowrap'
        >
            <Grid
                className={classes.header}
                container
                direction='row'
                item
                wrap='nowrap'
                xs={12}
            >
                <PageHeaderTitle
                    title={PAGE_TITLE}
                />
            </Grid>
            <Grid
                container
                direction='row'
            >
                <Grid
                    className={classes.accountSelectContainer}
                    item
                    xs={3}
                >
                    {accountSelectOptions.length > 1 ? 'tZERO Accounts' : 'tZERO Account'}
                    <StyledSelect
                        className={(isSelectDisabled || noAccounts) ? `${classes.accountSelect} ${classes.accountSelectDisabled}` : classes.accountSelect}
                        disabled={isSelectDisabled || noAccounts}
                        id='tZEROAccountSelect'
                        noOptionsMessage={() => <div className={classes.selectNoOptions}>{noOptionsText}</div>}
                        onChange={selectAccount}
                        options={accountSelectOptions}
                        placeholder={`${noAccounts ? noOptionsText : 'Select tZERO Account'}`}
                        value={accountSelectOptions.find(account => account.value === selectedAccount)}
                    />
                </Grid>
            </Grid>
            <div
                className='ag-theme-material'
                style={{
                    position: 'relative',
                    width: '100%',
                    height: '100%'
                }}
            >
                {(documentListStatus !== 'fetched' ||
                    (documentListStatus === 'fetched' && documents.length === 0)) && (
                    <TableOverlay text={getNoRowsText()} />
                )}
                <AgGridReact
                    animateRows={false}
                    columnDefs={columnDefs}
                    columnMenu='legacy'
                    components={{ downloadCellRenderer: DownloadCellRenderer }}
                    context={{ selectedDocuments }}
                    defaultColDef={{
                        resizable: false,
                        filter: false,
                        sortable: false,
                        minWidth: 80
                    }}
                    enableCellTextSelection={true}
                    getRowId={({ data }) => data.id}
                    onGridReady={onGridReady}
                    onRowClicked={downloadDocument}
                    rowData={documents}
                    suppressCellFocus={false}
                    suppressContextMenu={true}
                    // INFO: AG Grid v22 doesn't allow dynamic overlays, so suppress and use custom component (TableOverlay)
                    suppressNoRowsOverlay={true}
                />
            </div>
            <Snackbar
                autoHideDuration={5000}
                onClose={handleClose}
                open={isNotificationOpen}
            >
                <div className={snackbarContent.status === 'success' ? classes.notification : snackbarContent.status === 'error' ? classes.notificationError : ''}>
                    {snackbarContent.title}
                </div>
            </Snackbar>
        </Grid>
    );
};